import { Handle, Position } from 'reactflow';
import { type LucideIcon, Loader2, CheckCircle2, XCircle } from 'lucide-react';

interface BaseNodeUIProps {
    title: string;
    icon: LucideIcon;
    colorClass: string; // Used for Text/Icon color (e.g. text-teal-600)
    bgClass: string;   // Used for Border/Accent color (e.g. border-teal-500)
    children?: React.ReactNode;
    selected?: boolean;
    isSource?: boolean;
    isTarget?: boolean;
    hideHandles?: boolean;
    /** When true, show only left (input) and right (output) - no top/bottom. Matches Content Merger style (3 sides: left, right, bottom) when used with threeSidedHandles. */
    compactHandles?: boolean;
    /** When true with transform nodes, show 3 sides only: left, right, bottom (hide top). Matches Content Merger and other workflow nodes. */
    threeSidedHandles?: boolean;
    isRunning?: boolean;
    executionStatus?: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'Success' | 'Running' | 'Failed' | null;
    rowsProcessed?: number;
    className?: string;
}

export function BaseNodeUI({ title, icon: Icon, bgClass, children, selected, isSource = true, isTarget = true, hideHandles = false, compactHandles = false, threeSidedHandles = false, isRunning = false, executionStatus = null, className = "" }: BaseNodeUIProps) {
    // Redesign: Horizontal Card
    // - White Background
    // - Colored Icon Container
    // - Slate Text

    const isTransform = isSource && isTarget;
    const isOnlySource = isSource && !isTarget;
    const isOnlyTarget = !isSource && isTarget;

    // Extract color base from bgClass
    const colorMap: Record<string, { text: string; bg: string }> = {
        teal: { text: 'text-teal-600', bg: 'bg-teal-50' },
        blue: { text: 'text-blue-600', bg: 'bg-blue-50' },
        purple: { text: 'text-purple-600', bg: 'bg-purple-50' },
        red: { text: 'text-red-600', bg: 'bg-red-50' },
        orange: { text: 'text-orange-600', bg: 'bg-orange-50' },
        amber: { text: 'text-amber-600', bg: 'bg-amber-50' },
        yellow: { text: 'text-yellow-600', bg: 'bg-yellow-50' },
        lime: { text: 'text-lime-600', bg: 'bg-lime-50' },
        green: { text: 'text-green-600', bg: 'bg-green-50' },
        emerald: { text: 'text-emerald-600', bg: 'bg-emerald-50' },
        cyan: { text: 'text-cyan-600', bg: 'bg-cyan-50' },
        sky: { text: 'text-sky-600', bg: 'bg-sky-50' },
        indigo: { text: 'text-indigo-600', bg: 'bg-indigo-50' },
        violet: { text: 'text-violet-600', bg: 'bg-violet-50' },
        fuchsia: { text: 'text-fuchsia-600', bg: 'bg-fuchsia-50' },
        pink: { text: 'text-pink-600', bg: 'bg-pink-50' },
        rose: { text: 'text-rose-600', bg: 'bg-rose-50' },
        slate: { text: 'text-slate-600', bg: 'bg-slate-50' },
        gray: { text: 'text-gray-600', bg: 'bg-gray-50' },
        zinc: { text: 'text-zinc-600', bg: 'bg-zinc-50' },
        neutral: { text: 'text-neutral-600', bg: 'bg-neutral-50' },
        stone: { text: 'text-stone-600', bg: 'bg-stone-50' },
    };

    // Find matching color key based on bgClass inclusion
    const matchedColorKey = Object.keys(colorMap).find(c => bgClass.includes(c));
    const themeParams = matchedColorKey ? colorMap[matchedColorKey] : { text: 'text-slate-600', bg: 'bg-slate-50' };

    const accentText = themeParams.text;
    const accentBg = themeParams.bg;

    // Derived status - Fallback to isRunning for spinner if executionStatus not provided
    const status = executionStatus || (isRunning ? 'Running' : null);

    return (
        // Fixed outer size — all data-processing nodes are the same 200×64px.
        // Only container nodes (Loop, ForEach) use NodeResizer.
        <div className="relative flex flex-col items-center group w-[200px] h-[64px]">
            {/* Main Node Container - Horizontal Card Design */}
            <div
                className={`
                    relative flex items-center
                    w-full h-full rounded-xl shadow-sm border transition-all duration-300 z-10 
                    bg-white
                    ${selected ? 'border-yellow-400 ring-2 ring-yellow-400/20 shadow-md' : 'border-slate-200 hover:border-slate-300 hover:shadow-md'}
                    ${className}
                `}
            >
                {/* Colored Icon Container (Left) */}
                <div className={`
                    ml-3 h-10 w-10 flex items-center justify-center rounded-lg 
                    ${accentBg} ${accentText}
                `}>
                    <Icon className="h-5 w-5" strokeWidth={2} />
                </div>

                {/* Label (Right) */}
                <div className="ml-3 flex-1 overflow-hidden">
                    <div className="text-xs font-semibold text-slate-700 truncate leading-tight select-none" title={title}>
                        {title}
                    </div>
                    {/* Optional: Add a small status text or type if needed later */}
                </div>

                {/* Children render (for extra badges etc) */}
                {children && <div className="mr-2 pointer-events-auto scale-75 origin-right">{children}</div>}

                {/* Status Indicators */}
                {(status === 'RUNNING' || status === 'Running') && (
                    <div className="absolute -top-2 -right-2 p-1 bg-white rounded-full shadow-md z-20 border border-slate-200">
                        <Loader2 className="h-3.5 w-3.5 text-blue-600 animate-spin" strokeWidth={2.5} />
                    </div>
                )}

                {(status === 'COMPLETED' || status === 'Success') && (
                    <div className="absolute -top-2 -right-2 p-1 bg-green-600 rounded-full shadow-md z-20 border border-white">
                        <CheckCircle2 className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                    </div>
                )}

                {(status === 'FAILED' || status === 'Failed') && (

                    <div className="absolute -top-2 -right-2 p-1 bg-red-600 rounded-full shadow-md z-20 border border-white">
                        <XCircle className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                    </div>
                )}

                {/* --- HANDLES --- */}

                {/* --- SMART HANDLES (Hidden unless hovered/connected) --- */}
                {/* We add Target/Source handles on all sides. 
                    The Logic in FlowPage will switch edges to use the best handle.
                    We hide them by default to avoid clutter. 
                */}

                {/* TARGETS - always render inputs (except when hideHandles is true) so that any node can be orchestrated/triggered */}
                {!hideHandles && (
                    <>
                        <Handle type="target" position={Position.Left} id="in" className="!w-3 !h-3 !bg-white !border-2 !border-slate-400 z-50" style={{ left: -6 }} />
                        {!compactHandles && !threeSidedHandles && (
                            <Handle type="target" position={Position.Top} id="in-top" className="!w-3 !h-3 !bg-white !border-2 !border-slate-400 z-50 opacity-0 group-hover:opacity-100 transition-opacity" style={{ top: -6 }} />
                        )}
                        {(!compactHandles || threeSidedHandles) && (
                            <Handle type="target" position={Position.Bottom} id="in-bottom" className="!w-3 !h-3 !bg-white !border-2 !border-slate-400 z-50 opacity-0 group-hover:opacity-100 transition-opacity" style={{ bottom: -6 }} />
                        )}
                    </>
                )}

                {/* SOURCES - always render outputs (except when hideHandles is true) so that any node can trigger downstream sequences */}
                {!hideHandles && (
                    <>
                        <Handle
                            type="source" position={Position.Right} id={isTransform || isOnlyTarget ? "success" : "out"}
                            className="!w-3 !h-3 !bg-white !border-2 !border-emerald-500 z-50"
                            style={{ right: -6 }}
                        />
                        {!isOnlyTarget && !compactHandles && !threeSidedHandles && (
                            <>
                                <Handle
                                    type="source" position={Position.Bottom} id={isTransform ? "success-bottom" : "out-bottom"}
                                    className="!w-3 !h-3 !bg-white !border-2 !border-emerald-500 z-50 opacity-0 group-hover:opacity-100 transition-opacity"
                                    style={{ bottom: -6 }}
                                />
                                <Handle
                                    type="source" position={Position.Top} id={isTransform ? "success-top" : "out-top"}
                                    className="!w-3 !h-3 !bg-white !border-2 !border-emerald-500 z-50 opacity-0 group-hover:opacity-100 transition-opacity"
                                    style={{ top: -6 }}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        </div >
    );
}
